import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceVolumeTrendPoint } from '../../../services/audience-api.service';

type SeriesKey = 'existing' | 'new' | 'removed';

interface SeriesMeta {
  key: SeriesKey;
  color: string;
}

/** A segment placed by absolute top/height percentage within the (signed) plot area. */
interface PositionedSeg {
  key: SeriesKey;
  color: string;
  top: number;
  height: number;
}

interface DayBar {
  date: string;
  label: string;
  existing: number;
  new: number;
  removed: number;
  total: number;
  segments: PositionedSeg[];
}

interface AxisTick {
  pct: number;   // vertical position from the top of the plot (0..100)
  value: number; // signed value at that line
}

/**
 * Shared audience volumes-trend renderer (plain HTML/CSS, new-idiom rewrite of the admin-dashboard
 * `volumes-trend-chart` — no chart.js/chroma). Per-day bars: Existing / New (stacked upward); the
 * audience/activation volume endpoints only return days up to today, so there is no "future"
 * series. Removed users are drawn as a **negative** bar going BELOW a
 * zero baseline, so churn reads at a glance. Segments are absolutely positioned around a computed
 * zero line so the up/down layout stays exact.
 */
@Component({
  selector: 'app-audience-volumes-trend-chart',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './audience-volumes-trend-chart.component.html',
  styleUrls: ['./audience-volumes-trend-chart.component.scss']
})
export class AudienceVolumesTrendChartComponent implements OnChanges {
  @Input() volumesData: AudienceVolumeTrendPoint[] = [];
  /** Compact mode hides the y-axis, legend and x-labels (small embedded/preview charts). */
  @Input() compact = false;
  /** Optional explicit plot height (px); overrides the compact/full default. */
  @Input() heightPx: number | null = null;

  readonly series: SeriesMeta[] = [
    { key: 'existing', color: '#2563eb' },
    { key: 'new', color: '#22c55e' },
    { key: 'removed', color: '#ef4444' }
  ];
  /** Stack order (bottom→top) of the positive series, growing upward from the zero line. */
  private readonly positiveOrder: SeriesKey[] = ['existing', 'new'];

  bars: DayBar[] = [];
  zeroPct = 100;      // where value=0 sits (100 = bottom when there are no negatives)
  yTicks: AxisTick[] = [];
  hoveredDate: string | null = null;

  ngOnChanges(): void {
    this.rebuild();
  }

  private rebuild(): void {
    const rows: DayBar[] = (this.volumesData ?? []).map(point => {
      const date = this.dayString(point.Date);
      const volumes = this.num(point.Volumes);
      const newUsers = Math.max(0, this.num(point.NewUsers));
      const removed = Math.abs(this.num(point.RemovedUsers));
      const existing = Math.max(0, volumes - newUsers);
      return {
        date, label: this.shortLabel(date),
        existing, new: newUsers, removed,
        total: existing + newUsers + removed,
        segments: []
      };
    });

    // Signed layout: positives (existing+new) go up, removed goes down from a zero line.
    const maxPos = rows.reduce((m, b) => Math.max(m, b.existing + b.new), 0);
    const maxNeg = rows.reduce((m, b) => Math.max(m, b.removed), 0);
    const range = maxPos + maxNeg || 1;
    this.zeroPct = (maxPos / range) * 100;

    for (const b of rows) {
      const segments: PositionedSeg[] = [];
      let cumulative = 0; // value from zero, upward
      for (const key of this.positiveOrder) {
        const val = b[key];
        if (val > 0) {
          const topVal = cumulative + val;
          segments.push({
            key,
            color: this.colorFor(key),
            top: ((maxPos - topVal) / range) * 100,
            height: (val / range) * 100
          });
          cumulative = topVal;
        }
      }
      if (b.removed > 0) {
        segments.push({
          key: 'removed',
          color: this.colorFor('removed'),
          top: this.zeroPct,
          height: (b.removed / range) * 100
        });
      }
      b.segments = segments;
    }

    this.bars = rows;
    this.yTicks = maxNeg > 0
      ? [{ pct: 0, value: maxPos }, { pct: this.zeroPct, value: 0 }, { pct: 100, value: -maxNeg }]
      : [{ pct: 0, value: maxPos }, { pct: 50, value: Math.round(maxPos / 2) }, { pct: 100, value: 0 }];
  }

  private colorFor(key: SeriesKey): string {
    return this.series.find(s => s.key === key)?.color ?? '#9ca3af';
  }

  get hasNegative(): boolean {
    return this.bars.some(b => b.removed > 0);
  }

  /** Series that actually carry data (so an all-zero series doesn't clutter the legend). */
  get presentSeries(): SeriesMeta[] {
    return this.series.filter(s => this.bars.some(b => b[s.key] > 0));
  }

  seriesColor(key: SeriesKey): string {
    return this.colorFor(key);
  }

  get hasData(): boolean {
    return this.bars.some(b => b.total > 0);
  }

  onBarEnter(bar: DayBar): void {
    this.hoveredDate = bar.date;
  }

  onBarLeave(): void {
    this.hoveredDate = null;
  }

  trackByDate(_index: number, bar: DayBar): string {
    return bar.date;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('it-IT');
  }

  num(v: number | string | null | undefined): number {
    return Number(v) || 0;
  }

  private dayString(value: string | Date): string {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
      return String(value).slice(0, 10);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private shortLabel(date: string): string {
    const [, m, d] = date.split('-');
    return d && m ? `${d}/${m}` : date;
  }
}
